import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Link2, Gift, Check, Loader2, Gamepad2 } from 'lucide-react';

interface LinkedGame {
  gameId: string;
  gameName: string;
  gameUrl: string;
  userId: string | number;
  userPseudo: string;
  level: number;
  totalPoints: number;
}

interface LinkAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  linkedGames: LinkedGame[];
  benefits: string[];
  onLinkAccount: (gameId: string) => Promise<boolean>;
}

export default function LinkAccountsModal({ 
  isOpen, 
  onClose, 
  linkedGames, 
  benefits,
  onLinkAccount 
}: LinkAccountsModalProps) {
  const [linking, setLinking] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleLink = async (gameId: string) => {
    setLinking(gameId);
    setError(null);
    
    try {
      const success = await onLinkAccount(gameId);
      if (success) {
        setLinkedIds(prev => [...prev, gameId]);
      } else {
        setError('Erreur lors de la liaison');
      }
    } catch (err) {
      setError('Erreur lors de la liaison');
    } finally {
      setLinking(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="bg-dark-800 rounded-2xl p-6 w-full max-w-lg border border-dark-600 shadow-xl"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary-500/20">
                <Link2 className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Lier vos comptes</h2>
                <p className="text-sm text-dark-400">Nous avons trouvé votre email sur d'autres jeux !</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-dark-700 text-dark-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Comptes trouvés */}
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-medium text-dark-300">Comptes détectés :</h3>
            {linkedGames.map((game) => (
              <div 
                key={game.gameId}
                className="flex items-center justify-between p-4 bg-dark-700/50 rounded-xl border border-dark-600"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                    <Gamepad2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{game.gameName}</p>
                    <p className="text-sm text-dark-400">
                      {game.userPseudo} • Niveau {game.level} • {game.totalPoints.toLocaleString()} pts
                    </p>
                  </div>
                </div>
                
                {linkedIds.includes(game.gameId) ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <Check className="w-5 h-5" />
                    <span className="text-sm font-medium">Lié</span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleLink(game.gameId)}
                    disabled={linking !== null}
                    className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    {linking === game.gameId ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Lier'
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Avantages */}
          <div className="p-4 bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-xl border border-primary-500/20 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-primary-400" />
              <h3 className="font-medium text-white">Avantages de la liaison</h3>
            </div>
            <ul className="space-y-2">
              {benefits.map((benefit, index) => (
                <li key={index} className="text-sm text-dark-300 flex items-start gap-2">
                  <span className="mt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4">
              {error}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-white transition-colors"
            >
              {linkedIds.length > 0 ? 'Terminé' : 'Plus tard'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
